import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Heart, Search, Leaf, Package, Truck, User, LogOut, ShieldCheck, MapPin, ChevronDown, Menu, X } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext'; 
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import SideDrawer from './SideDrawer'; 
import toast from 'react-hot-toast';

export default function Navbar() {
  const { cart } = useCart();
  const { wishlist } = useWishlist(); 
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [user, setUser] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false); 
  const navigate = useNavigate();
  const auth = getAuth();

  // List of categories for the dropdown
  const categories = [
    'All Categories',
    'Eco-Essentials',
    'Zero-Waste Kits',
    'Biodegradable Packaging',
    'Circular Economy',
    'Plant-a-Tree Initiative'
  ];

  // Total items count in cart & wishlist
  const totalCartItems = cart.reduce((acc, item) => acc + item.quantity, 0);
  const totalWishlistItems = wishlist ? wishlist.length : 0; 

  // Track Firebase Authentication State
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, [auth]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim() || selectedCategory !== 'All Categories') {
      const categoryParam = selectedCategory !== 'All Categories' ? `&category=${encodeURIComponent(selectedCategory)}` : '';
      navigate(`/shop?search=${encodeURIComponent(searchQuery)}${categoryParam}`);
      setMobileMenuOpen(false);
    }
  };

  // Dynamic Professional Logout Handler with Confirmation Popup
  const handleLogout = () => {
    toast((t) => (
      <div className="flex flex-col space-y-3 p-1">
        <div className="flex items-center space-x-2">
          <span className="text-xl">👋</span>
          <div>
            <p className="font-bold text-gray-900 text-xs">Ready to leave?</p>
            <p className="text-[11px] text-gray-500">Are you sure you want to log out of your account?</p>
          </div>
        </div>
        <div className="flex space-x-2 pt-1">
          <button
            onClick={async () => {
              toast.dismiss(t.id);
              try {
                await signOut(auth);
                toast.success("Logged out successfully!", { icon: '🔒' });
                navigate('/'); // Updated to redirect to home page
              } catch (error) {
                toast.error("Error signing out: " + error.message);
              }
            }}
            className="flex-1 bg-rose-600 hover:bg-rose-700 text-white font-bold py-1.5 px-3 rounded-lg text-xs transition shadow"
          >
            Confirm Logout
          </button>
          <button
            onClick={() => toast.dismiss(t.id)}
            className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold py-1.5 px-3 rounded-lg text-xs transition"
          >
            Cancel
          </button>
        </div>
      </div>
    ), {
      duration: Infinity, 
      position: 'top-center',
    });
  };

  return (
    <header className="sticky top-0 z-50 flex flex-col shadow-md">
      
      {/* 1. Primary Amazon Header Row */}
      <div className="bg-[#131921] text-white px-4 py-2 flex items-center justify-between gap-2 sm:gap-4">
        
        {/* Left: Mobile Menu Toggle & Brand Logo */}
        <div className="flex items-center space-x-2 sm:space-x-6">
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-1 text-gray-300 hover:text-white"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>

          <Link to="/" className="flex items-center space-x-1.5 px-1 border border-transparent hover:border-white rounded-sm">
            <span className="font-black text-xl sm:text-2xl tracking-tighter text-white">
              Eco<span className="text-amber-400">Store</span>
            </span>
            <Leaf className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-400 inline" />
          </Link>

          {/* Location Selector */}
          <div className="hidden xl:flex items-center space-x-1 px-2 py-1 border border-transparent hover:border-white rounded-sm cursor-pointer text-xs">
            <MapPin className="h-4 w-4 text-gray-300 mt-2" />
            <div className="flex flex-col leading-tight">
              <span className="text-[10px] text-gray-400">Delivering to New Delhi 110098</span>
              <span className="font-bold text-white">Update location</span>
            </div>
          </div>
        </div>

        {/* Central Search Bar with Smooth CSS Hover Dropdown */}
        <form onSubmit={handleSearchSubmit} className="hidden md:flex flex-1 max-w-3xl items-center h-10 rounded-md bg-white relative">
          
          {/* Category Dropdown Container using Pure Group-Hover with Smooth Transition */}
          <div className="relative group h-full bg-gray-100 text-gray-700 px-3 flex items-center space-x-1 text-xs border-r border-gray-300 cursor-pointer hover:bg-gray-200 rounded-l-md">
            <span className="truncate max-w-[110px]">{selectedCategory}</span>
            <ChevronDown className="h-3 w-3 text-gray-500 flex-shrink-0 transition-transform duration-200 group-hover:rotate-180" />
            
            {/* Smooth Animated Dropdown Menu List */}
            <div className="absolute left-0 top-full w-48 bg-white text-gray-900 border border-gray-200 rounded-md shadow-2xl py-2 z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform translate-y-1 group-hover:translate-y-0">
              {categories.map((cat, index) => (
                <div 
                  key={index} 
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-4 py-2 hover:bg-emerald-50 hover:text-emerald-700 text-xs cursor-pointer transition-colors duration-150 ${selectedCategory === cat ? 'font-bold bg-gray-50 text-emerald-600' : ''}`}
                >
                  {cat}
                </div>
              ))}
            </div>
          </div>

          <input 
            type="text" 
            placeholder="Search EcoStore.in" 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-3 py-2 text-sm text-gray-900 focus:outline-none bg-transparent"
          />
          <button type="submit" className="bg-amber-400 hover:bg-amber-500 text-gray-950 px-5 h-full flex items-center justify-center transition-colors rounded-r-md">
            <Search className="h-5 w-5 font-bold stroke-[2.5]" />
          </button>
        </form>

        {/* Right Side Options */}
        <div className="flex items-center space-x-1 sm:space-x-3 text-xs">
          
          {/* Language Selector with Hover Dropdown */}
          <div className="relative group hidden lg:block cursor-pointer">
            <div className="flex items-center space-x-1 px-2 py-1.5 border border-transparent group-hover:border-white rounded-sm font-bold">
              <span>🇮🇳 EN</span>
              <ChevronDown className="h-3 w-3 text-gray-400" />
            </div>
            <div className="absolute right-0 top-full w-36 bg-white text-gray-900 border border-gray-200 rounded-md shadow-xl py-2 hidden group-hover:block z-50">
              <div className="px-4 py-1.5 hover:bg-gray-100 font-bold text-xs cursor-pointer">English - EN</div>
              <div className="px-4 py-1.5 hover:bg-gray-100 text-xs cursor-pointer">Hindi - HI (हिंदी)</div>
            </div>
          </div>

          {/* Account & Lists with HOVER Dropdown */}
          <div className="relative group cursor-pointer">
            {user ? (
              <div className="flex flex-col text-left px-2 py-1 border border-transparent group-hover:border-white rounded-sm">
                <span className="text-[10px] text-gray-300 truncate max-w-[90px]">Hello, {user.email?.split('@')[0]}</span>
                <span className="font-bold text-white flex items-center">Account & Lists <ChevronDown className="h-3 w-3 ml-0.5 text-gray-400" /></span>
              </div>
            ) : (
              <Link to="/login" className="flex flex-col text-left px-2 py-1 border border-transparent hover:border-white rounded-sm">
                <span className="text-[10px] text-gray-300">Hello, sign in</span>
                <span className="font-bold text-white flex items-center">Account & Lists <ChevronDown className="h-3 w-3 ml-0.5 text-gray-400" /></span>
              </Link>
            )}

            {/* Hover Dropdown Menu */}
            {user && (
              <div className="absolute right-0 top-full w-52 bg-white text-gray-900 border border-gray-200 rounded-md shadow-2xl py-3 hidden group-hover:block z-50">
                <div className="px-4 py-2 border-b border-gray-100 text-[10px] text-gray-500 truncate">
                  Signed in as <br /><span className="font-bold text-gray-900">{user.email}</span>
                </div>
                <Link to="/profile" className="px-4 py-2.5 hover:bg-gray-100 flex items-center space-x-2 text-xs font-semibold text-indigo-600">
                  <User className="h-4 w-4" />
                  <span>My Profile</span>
                </Link>
                <Link to="/my-orders" className="px-4 py-2.5 hover:bg-gray-100 flex items-center space-x-2 text-xs font-semibold">
                  <Package className="h-4 w-4 text-gray-600" />
                  <span>Your Orders</span>
                </Link>
                <button onClick={handleLogout} className="w-full text-left px-4 py-2.5 hover:bg-gray-100 flex items-center space-x-2 font-bold text-rose-600 text-xs border-t border-gray-100 mt-1">
                  <LogOut className="h-4 w-4" />
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>

          {/* Returns & Orders */}
          <Link to="/my-orders" className="hidden sm:flex flex-col px-2 py-1 border border-transparent hover:border-white rounded-sm">
            <span className="text-[10px] text-gray-300">Returns</span>
            <span className="font-bold text-white">& Orders</span>
          </Link>

          {/* PROFESSIONAL COMPACT WISHLIST BUTTON */}
          <Link to="/profile" className="flex items-center px-2 py-1.5 border border-transparent hover:border-white/40 rounded-md transition relative group" title="Wishlist">
            <div className="relative flex items-center justify-center p-1 bg-white/5 group-hover:bg-white/10 rounded">
              <Heart className="h-5 w-5 text-rose-500 fill-current transition-transform group-hover:scale-105" />
              {totalWishlistItems > 0 && (
                <span className="absolute -top-1.5 -right-1.5 bg-amber-400 text-gray-950 font-black text-[9px] h-3.5 w-3.5 rounded-full flex items-center justify-center shadow">
                  {totalWishlistItems}
                </span>
              )}
            </div>
            <span className="font-bold text-white ml-1.5 hidden sm:inline text-xs tracking-tight">Wishlist</span>
          </Link>

          {/* Cart Icon */}
          <Link to="/cart" className="flex items-center px-2 py-1.5 border border-transparent hover:border-white/40 rounded-md transition relative group">
            <div className="relative flex items-center justify-center p-1">
              <ShoppingCart className="h-5 w-5 text-white transition-transform group-hover:scale-105" />
              {totalCartItems > 0 && (
                <span className="absolute -top-1.5 -right-1.5 bg-amber-400 text-gray-950 font-black text-[9px] h-3.5 w-3.5 rounded-full flex items-center justify-center shadow">
                  {totalCartItems}
                </span>
              )}
            </div>
            <span className="font-bold text-white ml-1.5 hidden sm:inline text-xs tracking-tight">Cart</span>
          </Link>

        </div>

      </div>

      {/* Mobile Search Row */}
      <div className="md:hidden bg-[#131921] px-3 pb-2 pt-1">
        <form onSubmit={handleSearchSubmit} className="flex items-center h-9 rounded-md bg-white">
          <input 
            type="text" 
            placeholder="Search EcoStore.in" 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-3 text-xs text-gray-900 focus:outline-none bg-transparent"
          />
          <button type="submit" className="bg-amber-400 px-4 h-full flex items-center justify-center text-gray-950 rounded-r-md">
            <Search className="h-4 w-4 font-bold" />
          </button>
        </form>
      </div>

      {/* 2. Secondary Sub-Navbar Row */}
      <div className="bg-[#232f3e] text-white text-xs px-4 py-2 flex items-center space-x-4 sm:space-x-6 overflow-x-auto whitespace-nowrap shadow-inner">
        <button 
          onClick={() => setIsDrawerOpen(true)}
          className="flex items-center space-x-1 font-bold px-2 py-1 border border-transparent hover:border-white rounded-sm cursor-pointer bg-transparent text-white"
        >
          <Menu className="h-4 w-4" />
          <span>All</span>
        </button>
        <Link to="/shop" className="px-2 py-1 border border-transparent hover:border-white rounded-sm text-emerald-300 font-semibold">🌱 Bestselling Eco-Essentials</Link>
        <Link to="/shop" className="px-2 py-1 border border-transparent hover:border-white rounded-sm">Zero-Waste Kits</Link>
        <Link to="/shop" className="px-2 py-1 border border-transparent hover:border-white rounded-sm">Biodegradable Packaging</Link>
        <Link to="/shop" className="px-2 py-1 border border-transparent hover:border-white rounded-sm">Circular Economy</Link>
        <Link to="/track-order" className="px-2 py-1 border border-transparent hover:border-white rounded-sm text-amber-300 font-bold">Track Order</Link>
        <Link to="/shop" className="px-2 py-1 border border-transparent hover:border-white rounded-sm">Plant-a-Tree Initiative</Link>
      </div>

      {/* Mobile Dropdown Menu Drawer */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white text-gray-900 border-b border-gray-200 py-3 px-4 shadow-xl space-y-3 text-xs font-semibold">
          {user && (
            <Link to="/profile" onClick={() => setMobileMenuOpen(false)} className="flex items-center space-x-2 py-2 border-b border-gray-100 text-indigo-600 font-bold">
              <User className="h-4 w-4" />
              <span>Customer Profile Dashboard</span>
            </Link>
          )}
          <Link to="/profile" onClick={() => setMobileMenuOpen(false)} className="flex items-center space-x-2 py-2 border-b border-gray-100 text-rose-600 font-bold">
            <Heart className="h-4 w-4 fill-current" />
            <span>Wishlist ({totalWishlistItems})</span>
          </Link>
          <Link to="/shop" onClick={() => setMobileMenuOpen(false)} className="block py-2 border-b border-gray-100">Browse Shop Directory</Link>
          <Link to="/track-order" onClick={() => setMobileMenuOpen(false)} className="block py-2 border-b border-gray-100">Track Your Order</Link>
          <Link to="/my-orders" onClick={() => setMobileMenuOpen(false)} className="block py-2 border-b border-gray-100">Your Orders</Link>
          {user ? (
            <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="block py-2 text-rose-600 font-bold text-left w-full">Logout ({user.email})</button>
          ) : (
            <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-emerald-800 font-bold">Sign In / Register</Link>
          )}
        </div>
      )}

      {/* SideDrawer Integration */}
      <SideDrawer isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} />

    </header>
  );
}